import { TRANSLATIONS } from '../../constants/translations';
import { AppData, StudioComponent } from '@wix/document-services-types';
import type {
  CustomActionConfig,
  ExternalLinkActionConfig,
} from '@wix/platform-editor-sdk';
import type {
  AppAction,
  AppActions,
  AppPremiumBannerInfo,
} from './platform.types';
import _ from 'lodash';
import { Delete, HelpCircleSmall } from '@wix/wix-ui-icons-common';
import { AUTOMATION_IDS } from '../../constants/automationIds';
import {
  APP_MANAGER_ORIGIN,
  DEFAULT_ACTIONS_ICONS,
  DEFAULT_ACTIONS,
} from '../../constants/constants';
import {
  getDefaultAppDescriptor,
  actionsMapper,
} from '../../utils/appDescriptor';
import { App, AppManagerApiDependencies } from '../../../../types/appManager';
import { t } from '../../../../essentials/i18n';
import { isUpdateAvailable } from '../appMarket/appMarket.utils';

type ActionConfig = CustomActionConfig | ExternalLinkActionConfig;

export const GENERAL_ACTION_IDS = {
  LEARN_MORE: 'LEARN_MORE',
  DELETE: 'DELETE',
  UPDATE: 'UPDATE',
} as const;

interface GeneralAction extends AppAction {
  condition: () => boolean;
}

type GetAppGeneralActions = (
  appDefId: string,
  deps: AppManagerApiDependencies,
) => Promise<AppAction[]>;

interface PrivateAppData extends AppData {
  latestVersion: string;
  displayVersion: string;
  blocksVersion?: string;
  canEdit?: boolean;
  isPublished?: boolean;
  components?: StudioComponent[];
}

const getAppGeneralActions: GetAppGeneralActions = async (appDefId, deps) => {
  const appDescriptor = await getDefaultAppDescriptor(appDefId, deps, t);
  const defaultActions = appDescriptor?.defaultActions || {};

  const config: GeneralAction[] = [
    {
      id: GENERAL_ACTION_IDS.LEARN_MORE,
      condition: () => !!defaultActions.learnMoreKB,
      onClick: () => deps.openHelpCenter(defaultActions.learnMoreKB!),
      title: t(
        TRANSLATIONS.APP_PAGE.MORE_ACTIONS_SECTION.ACTIONS_LABELS.LEARN_MORE,
      ),
      symbol: HelpCircleSmall,
      dataHook: AUTOMATION_IDS.APP_PAGE.LEARN_MORE_ACTION,
    },
    {
      id: GENERAL_ACTION_IDS.DELETE,
      condition: () => defaultActions.uninstall !== 'HIDE',
      onClick: () => {
        deps.onUninstallApp(appDefId, true);
      },
      title: t(
        TRANSLATIONS.APP_PAGE.MORE_ACTIONS_SECTION.ACTIONS_LABELS.DELETE_APP,
      ),
      symbol: Delete,
      dataHook: AUTOMATION_IDS.APP_PAGE.REMOVE_APP_ACTION,
    },
  ];

  return config.filter((action) => action.condition());
};

export const isDescriptorExists = (
  appDefId: string,
  deps: {
    getAppDescriptor: AppManagerApiDependencies['getAppDescriptor'];
  },
) => {
  const pageDescriptor = deps.getAppDescriptor(appDefId);
  return pageDescriptor
    ? Object.values(pageDescriptor).find((value) => !_.isEmpty(value))
    : false;
};

export const getAppActions = async (
  app: App,
  deps: AppManagerApiDependencies,
): Promise<AppActions> => {
  const { id: appDefId } = app;
  const appDescriptor = await getDefaultAppDescriptor(appDefId, deps, t);

  const handleActionClick = (
    action: CustomActionConfig | ExternalLinkActionConfig,
  ) => {
    if ('url' in action) {
      window.open(action.url, '_blank');
    } else if (Object.values(DEFAULT_ACTIONS).includes(action.actionId)) {
      actionsMapper(appDefId, deps, action.actionId);
    } else {
      deps.notifyApplication(appDefId, action.actionId);
    }
  };

  const mapPlatformActionToAppAction = (
    action: CustomActionConfig | ExternalLinkActionConfig,
    dataHook: any,
  ): AppAction => {
    const id = 'actionId' in action ? action.actionId : action.url;

    return {
      id,
      title: action.title,
      iconName: action.icon,
      onClick: () => handleActionClick(action),
      dataHook,
      permissions: deps.addGrantedToPermissions(action.permissions),
    };
  };

  const updateAction: AppAction[] = isUpdateAvailable(app, deps)
    ? [
        {
          id: GENERAL_ACTION_IDS.UPDATE,
          title: t(TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.UPDATE_APP),
          iconName: DEFAULT_ACTIONS_ICONS.UPDATE_APP,
          onClick: () => app.version && deps.onUpdateApp(appDefId, app.version),
          dataHook: AUTOMATION_IDS.APP_PAGE.UPDATE_ACTION,
          buttonText: t(
            TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.UPDATE_APP_BUTTON,
          ),
        },
      ]
    : [];

  const primaryActions = (appDescriptor.mainActions as ActionConfig[]).map(
    (action) =>
      mapPlatformActionToAppAction(
        action,
        AUTOMATION_IDS.APP_PAGE.PRIMARY_ACTION,
      ),
  );
  const secondaryActions = appDescriptor.customActions.map(
    (action: CustomActionConfig | ExternalLinkActionConfig) =>
      mapPlatformActionToAppAction(
        action,
        AUTOMATION_IDS.APP_PAGE.SECONDARY_ACTION,
      ),
  );

  const generalActions = await getAppGeneralActions(appDefId, deps);

  return {
    primary: updateAction.concat(primaryActions),
    secondary: secondaryActions,
    general: generalActions,
  };
};

export const getAppPremiumBannerInfo = async (
  appDefId: string,
  deps: AppManagerApiDependencies,
): Promise<AppPremiumBannerInfo | null> => {
  const appDescriptor = await getDefaultAppDescriptor(appDefId, deps, t);

  const upgradeConfig = appDescriptor?.defaultActions?.upgrade;

  if (!upgradeConfig) {
    return null;
  }

  const isSiteUpgradeConfig = upgradeConfig.upgradeType === 'SITE_UPGRADE';

  if (isSiteUpgradeConfig && deps.isPremium()) {
    return null;
  }

  const premiumOrigin = `${APP_MANAGER_ORIGIN}_${appDefId}`;

  const premiumUrl = isSiteUpgradeConfig
    ? deps.getSitePremiumUrl(appDefId, origin)
    : deps.getAppPremiumUrl(appDefId, origin, premiumOrigin);

  return {
    upgrade: () => window.open(premiumUrl, '_blank'),
    text: upgradeConfig.upgradeText,
    linkText: upgradeConfig.upgradeLinkText,
  };
};
